import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: object;
}

export default function SEOHead({
  title = "Vaal Cruising Association - Premier Boating Club | Vaal Dam | South Africa",
  description = "Join the Vaal Cruising Association - South Africa's premier boating club established in 1968. Located on the beautiful Vaal Dam offering membership, events, boat facilities, and cruising adventures.",
  keywords = "vaal cruising association, vca, boating club, vaal dam, south africa, boat club, cruising, sailing, powerboat, boat membership, water sports, marina",
  canonicalUrl = "https://vaalcruising.co.za/",
  ogImage = "https://vaalcruising.co.za/og-image.jpg",
  structuredData
}: SEOHeadProps) {
  useEffect(() => {
    // Update page title
    document.title = title;
    
    // Update meta descriptions
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', description);
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', description);
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', description);
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) metaKeywords.setAttribute('content', keywords);
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) canonicalLink.setAttribute('href', canonicalUrl);
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    
    const ogImageTag = document.querySelector('meta[property="og:image"]');
    if (ogImageTag) ogImageTag.setAttribute('content', ogImage);
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) twitterUrl.setAttribute('content', canonicalUrl);
    
    const twitterImage = document.querySelector('meta[property="twitter:image"]');
    if (twitterImage) twitterImage.setAttribute('content', ogImage);
    
    // Add structured data if provided
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo="structured-data"]');
      if (existingScript) existingScript.remove();
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'structured-data');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, ogImage, structuredData]);

  return null;
}

// Page-specific SEO configurations
export const homePageSEO = {
  title: "Vaal Cruising Association - Premier Boating Club | Vaal Dam | South Africa",
  description: "Join the Vaal Cruising Association - South Africa's premier boating club established in 1968. Located on the beautiful Vaal Dam offering membership, events, boat facilities, and cruising adventures.",
  keywords: "vaal cruising association, vca, boating club, vaal dam, south africa, boat club, cruising, sailing, powerboat, boat membership, water sports, marina, gauteng boating",
  canonicalUrl: "https://vaalcruising.co.za/"
};

export const membershipPageSEO = {
  title: "Join VCA - Membership Benefits & Application | Vaal Cruising Association",
  description: "Become a member of the Vaal Cruising Association. Enjoy exclusive access to boat facilities, events, and the premier boating community on Vaal Dam. Multiple membership options available.",
  keywords: "vca membership, join vaal cruising association, boat club membership, vaal dam membership, boating membership south africa",
  canonicalUrl: "https://vaalcruising.co.za/membership"
};

export const aboutPageSEO = {
  title: "About VCA - History & Mission | Vaal Cruising Association",
  description: "Learn about the Vaal Cruising Association's rich history since 1968, our mission to promote boating excellence, and our commitment to the Vaal Dam boating community.",
  keywords: "vca history, about vaal cruising association, boating club history, vaal dam history, south africa boating heritage",
  canonicalUrl: "https://vaalcruising.co.za/about"
};

export const historyPageSEO = {
  title: "VCA History - 55+ Years of Boating Excellence | Vaal Cruising Association",
  description: "Discover the rich heritage of the Vaal Cruising Association, established in 1968. Over 55 years of promoting boating culture and community on the Vaal Dam.",
  keywords: "vca history, vaal cruising association history, vaal dam boating history, 1968 boating club, south africa maritime heritage",
  canonicalUrl: "https://vaalcruising.co.za/history"
};

export const eventsPageSEO = {
  title: "VCA Events & Activities - Boating Events | Vaal Cruising Association",
  description: "Join exciting boating events and activities at the Vaal Cruising Association. Regattas, social events, training courses, and community gatherings on the Vaal Dam.",
  keywords: "vca events, boating events vaal dam, sailing regatta, boat club activities, vaal dam events, boating competitions",
  canonicalUrl: "https://vaalcruising.co.za/events"
};

export const contactPageSEO = {
  title: "Contact VCA - Get in Touch | Vaal Cruising Association",
  description: "Contact the Vaal Cruising Association for membership inquiries, event information, or general questions. Located on the beautiful Vaal Dam in South Africa.",
  keywords: "contact vca, vaal cruising association contact, boat club contact, vaal dam contact, vca phone number, vca email",
  canonicalUrl: "https://vaalcruising.co.za/contact"
};